import React from "react";
import { Link } from "react-router-dom";
import { Award, BookOpen, Building, CheckCircle2, GraduationCap, ScrollText } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { COMPANY } from "../lib/constants";

export default function About() {
  return (
    <main data-testid="about-page">
      {/* HEADER */}
      <section className="bg-[var(--m-bg-2)] section-pad pt-32 md:pt-44">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <span className="eyebrow">About Agartha Construction</span>
            <div className="gold-divider mt-4" />
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl mt-6 leading-[1] tracking-tight">
              An engineer-led<br />
              <em className="italic text-[var(--m-teal)]">construction firm</em><br />
              built on government-grade discipline.
            </h1>
          </div>
          <div className="md:col-span-5 flex items-end">
            <p className="text-[var(--m-ink-2)] leading-relaxed text-lg">
              Agartha Construction was founded with a simple conviction: every home, office and warehouse
              in Bengaluru deserves the same drawing rigour, structural integrity and BBMP compliance
              that government multi-storey housing receives.
            </p>
          </div>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section className="section-pad" data-testid="about-founder">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <img
              src="https://images.pexels.com/photos/8961031/pexels-photo-8961031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800"
              alt="Engineer reviewing structural plans"
              className="w-full h-[640px] object-cover"
            />
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7">
            <span className="eyebrow">The Proprietor</span>
            <div className="gold-divider mt-4" />
            <h2 className="font-display text-4xl md:text-5xl mt-6 leading-[1.05]">
              Mr. Kiran K, <span className="italic text-[var(--m-teal)]">C.Eng (Civil)</span>
            </h2>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                ["Chartered Engineer (Civil)", "Awarded April 2023", Award],
                ["Registered BBMP Engineer", "Obtained July 2023", ScrollText],
                ["B.E. Civil Engineering", "UVCE, Bangalore University", GraduationCap],
                ["Govt. of Karnataka", "RGHCL — since April 2018", Building],
              ].map(([t, s, Icon]) => (
                <div key={t} className="border-l-2 border-[var(--m-gold)] pl-5">
                  <Icon size={20} className="text-[var(--m-teal)] mb-3" strokeWidth={1.4} />
                  <div className="font-display text-xl">{t}</div>
                  <div className="text-xs tracking-wide text-[var(--m-ink-3)] mt-1 uppercase">{s}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 space-y-5 text-[var(--m-ink-2)] leading-relaxed">
              <p>
                Since April 2018, Mr. Kiran has served as Junior / Assistant Engineer with{" "}
                <strong className="text-[var(--m-ink)]">Rajiv Gandhi Housing Corporation Limited</strong>,
                a Government of Karnataka undertaking. He has been a core part of the historic{" "}
                <em>Bengaluru Housing Programme</em> — overseeing detailed project
                reports, architectural drawings, structural design, and on-site execution from G+3
                up to S+14 floors.
              </p>
              <p>
                His daily work covers BBMP plan sanctions, NoCs from multiple departments,
                quality control on concrete and reinforcement, and supervision of advanced construction
                technologies including <strong>MIVAN, Aluminium Formwork, Shear Wall and Monolithic
                Construction</strong>. He holds working expertise in foundation systems spanning
                Isolated, Combined, Raft, Strip and Pile foundations.
              </p>
              <p>
                Agartha Construction is the natural extension of this discipline into the private
                sector — bringing engineer-grade processes to villas, apartments, commercial buildings
                and PEB structures.
              </p>
            </div>

            <div className="mt-10 flex gap-3 flex-wrap">
              {["AUTOCAD", "REVIT Architecture", "TEKLA Structures", "MIVAN", "Shear Wall", "Monolithic", "BBMP Approvals"].map((s) => (
                <span key={s} className="tag">{s}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY DIFFERENT */}
      <section className="section-pad bg-[var(--m-ink)] text-white" data-testid="about-why">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="text-[var(--m-gold)] text-xs tracking-[0.32em] uppercase">Why Agartha is different</span>
            <div className="gold-divider mt-4" />
            <h2 className="font-display text-4xl md:text-5xl mt-6 leading-[1.05]">
              "This is not just another contractor company.<br />
              <em className="italic text-[var(--m-gold)]">This is an engineer-led premium construction solution firm.</em>"
            </h2>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {[
              {
                t: "Engineer-Led, Not Contractor-Led",
                d: "A Chartered Civil Engineer with Govt. of Karnataka experience signs off on every structural drawing, every approval and every concrete mix design.",
                icon: BookOpen,
              },
              {
                t: "Government-Grade Standards",
                d: "We apply the same IS-code QC processes used on multi-storey housing projects to your villa, apartment or warehouse.",
                icon: ShieldIcon,
              },
              {
                t: "Single Accountable Firm",
                d: "Drawings, approvals, structural work, interiors, PEB and handover — one team, one contract, one engineer.",
                icon: CheckCircle2,
              },
            ].map(({ t, d, icon: Icon }) => (
              <div key={t} className="bg-[var(--m-ink)] p-10">
                <Icon size={26} className="text-[var(--m-gold)] mb-5" strokeWidth={1.4} />
                <h3 className="font-display text-2xl">{t}</h3>
                <p className="mt-4 text-sm text-white/70 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad" data-testid="about-cta">
        <div className="container-x text-center max-w-3xl mx-auto">
          <h2 className="font-display text-4xl md:text-5xl">Build with the engineer who built government housing.</h2>
          <p className="mt-5 text-[var(--m-ink-2)]">{COMPANY.tagline}</p>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link to="/quotation" className="btn-primary">Request a Quotation</Link>
            <Link to="/contact" className="btn-secondary">Get in Touch</Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function ShieldIcon(props) {
  return (
    <svg width={props.size || 24} height={props.size || 24} viewBox="0 0 24 24" fill="none" stroke={props.className?.includes("gold") ? "#C4A454" : "currentColor"} strokeWidth={props.strokeWidth || 1.4} strokeLinecap="round" strokeLinejoin="round" className={props.className}>
      <path d="M12 2 L4 5 V12 C4 17 8 21 12 22 C16 21 20 17 20 12 V5 Z" />
      <path d="M9 12 L11 14 L15 10" />
    </svg>
  );
}
